import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import { logActivity } from "@/lib/logActivity";
import { type Lead, DEFAULT_JOB_STEPS, fmtDate } from "../types";

/* ─── Constants ─────────────────────────────────────────────── */
const JOB_TYPES = [
  "Wedding", "Portrait", "Engagement", "Family", "Newborn",
  "Event", "Corporate", "Product", "Real Estate", "Boudoir", "Other",
];

const APPT_TYPES = [
  "Consultation", "Site Visit", "Pre-shoot Meeting", "Delivery Meeting", "Other",
];

const INPUT = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all";

/* ─── Types ─────────────────────────────────────────────────── */
interface Props {
  lead: Lead;
  tenantId: string;
  onLeadUpdate: (updated: Partial<Lead>) => void;
}

type Panel = "convert" | "job" | "contract" | "appointment" | null;

interface ClientInfo {
  id:        string;
  full_name: string;
  email:     string | null;
  phone:     string | null;
  isNew:     boolean;
}

/* ─── Core: find-or-create client ───────────────────────────── */
async function resolveClient(
  lead: Lead,
  tenantId: string,
): Promise<{ client: ClientInfo | null; error?: string }> {
  // 1. Search by email
  if (lead.email) {
    const { data: byEmail } = await supabase
      .from("clients")
      .select("id, full_name, email, phone")
      .eq("tenant_id", tenantId)
      .eq("email", lead.email)
      .maybeSingle();
    if (byEmail) {
      return {
        client: { ...(byEmail as { id: string; full_name: string; email: string | null; phone: string | null }), isNew: false },
      };
    }
  }

  // 2. Search by phone
  if (lead.phone) {
    const { data: byPhone } = await supabase
      .from("clients")
      .select("id, full_name, email, phone")
      .eq("tenant_id", tenantId)
      .eq("phone", lead.phone)
      .maybeSingle();
    if (byPhone) {
      return {
        client: { ...(byPhone as { id: string; full_name: string; email: string | null; phone: string | null }), isNew: false },
      };
    }
  }

  // 3. Create new client
  const notes = [
    lead.event_type ? `Event type: ${lead.event_type}` : null,
    lead.event_date ? `Event date: ${fmtDate(lead.event_date)}` : null,
    lead.message    ? `Lead message: ${lead.message}` : null,
  ].filter(Boolean).join("\n");

  const { data: created, error } = await supabase
    .from("clients")
    .insert({
      tenant_id: tenantId,
      full_name: lead.name,
      email:     lead.email  ?? null,
      phone:     lead.phone  ?? null,
      notes:     notes || null,
      status:    "active",
    })
    .select("id, full_name, email, phone")
    .maybeSingle();

  if (error || !created) {
    return { client: null, error: error?.message ?? "Failed to create client" };
  }

  return {
    client: { ...(created as { id: string; full_name: string; email: string | null; phone: string | null }), isNew: true },
  };
}

/* ─── Mark lead as booked + set converted_client_id ─────────── */
async function markConverted(
  lead: Lead,
  clientId: string,
  onLeadUpdate: (p: Partial<Lead>) => void,
) {
  const payload = {
    status:               "booked",
    converted_client_id:  clientId,
    updated_at:           new Date().toISOString(),
  };
  await supabase
    .from("tenant_public_leads")
    .update(payload)
    .eq("id", lead.id)
    .eq("tenant_id", lead.tenant_id);
  onLeadUpdate(payload);
}

/* ─── Component ──────────────────────────────────────────────── */
export default function ConversionTab({ lead, tenantId, onLeadUpdate }: Props) {
  const navigate     = useNavigate();
  const [panel, setPanel]   = useState<Panel>(null);
  const [saving, setSaving] = useState(false);

  /* Linked client info (if lead already converted) */
  const [linkedClient, setLinkedClient] = useState<ClientInfo | null>(null);
  const [loadingClient, setLoadingClient] = useState(false);

  /* Job form */
  const [jobTitle,    setJobTitle]    = useState(lead.event_type ? `${lead.event_type} — ${lead.name}` : lead.name);
  const [jobType,     setJobType]     = useState(lead.event_type ?? "Wedding");
  const [jobDate,     setJobDate]     = useState(lead.event_date ?? "");
  const [jobLocation, setJobLocation] = useState("");
  const [jobNotes,    setJobNotes]    = useState(lead.message ?? "");

  /* Contract form */
  const [ctTitle,    setCtTitle]    = useState(lead.event_type ? `${lead.event_type} — ${lead.name}` : lead.name);
  const [ctLocation, setCtLocation] = useState("");
  const [ctPackage,  setCtPackage]  = useState("");
  const [ctTotal,    setCtTotal]    = useState("");
  const [ctDeposit,  setCtDeposit]  = useState("");

  /* Appointment form */
  const [apptTitle,    setApptTitle]    = useState(`Consultation — ${lead.name}`);
  const [apptType,     setApptType]     = useState("Consultation");
  const [apptDate,     setApptDate]     = useState("");
  const [apptTime,     setApptTime]     = useState("10:00");
  const [apptLocation, setApptLocation] = useState("");
  const [apptNotes,    setApptNotes]    = useState("");

  /* Load linked client if already converted */
  const fetchLinkedClient = useCallback(async () => {
    if (!lead.converted_client_id) return;
    setLoadingClient(true);
    const { data } = await supabase
      .from("clients")
      .select("id, full_name, email, phone")
      .eq("id", lead.converted_client_id)
      .eq("tenant_id", tenantId)
      .maybeSingle();
    if (data) {
      setLinkedClient({ ...(data as { id: string; full_name: string; email: string | null; phone: string | null }), isNew: false });
    }
    setLoadingClient(false);
  }, [lead.converted_client_id, tenantId]);

  useEffect(() => { fetchLinkedClient(); }, [fetchLinkedClient]);

  /* ── Convert to client ── */
  const handleConvert = async () => {
    setSaving(true);

    // First, check if client already exists
    const { client, error } = await resolveClient(lead, tenantId);
    setSaving(false);

    if (error || !client) {
      Swal.fire({ title: "Error", text: error ?? "Unexpected error", icon: "error", background: "#ffffff", color: "#0f172a" });
      return;
    }

    // Build confirm message based on whether client exists
    const isExisting = !client.isNew;
    const result = await Swal.fire({
      title: isExisting ? "Client already exists" : `Convert "${lead.name}"?`,
      html: isExisting
        ? `<p style="color:#64748b;font-size:13px">A client named <strong style="color:#0f172a">"${client.full_name}"</strong> with matching ${lead.email ? "email" : "phone"} was found.<br><br>The lead will be linked to this existing client and marked as <strong>Booked</strong>.</p>`
        : `<p style="color:#64748b;font-size:13px">A new client record will be created for <strong style="color:#0f172a">"${lead.name}"</strong>.<br>Lead will be marked as <strong>Booked</strong>.</p>`,
      icon: isExisting ? "info" : "question",
      showCancelButton: true,
      confirmButtonText: isExisting ? "Link to existing client" : "Yes, convert",
      cancelButtonText: "Cancel",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#C8A96E",
      cancelButtonColor: "#64748b",
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    await markConverted(lead, client.id, onLeadUpdate);
    logActivity({
      tenantId,
      action:     "created",
      entity:     "client",
      entityId:   client.id,
      entityName: client.full_name,
    });
    setLinkedClient(client);
    setSaving(false);
    setPanel(null);

    Swal.fire({
      title: isExisting ? "Lead linked!" : "Client created!",
      html: isExisting
        ? `<p style="color:#64748b;font-size:13px">Lead linked to existing client <strong style="color:#9a7840">"${client.full_name}"</strong> and marked as Booked.</p>`
        : `<p style="color:#64748b;font-size:13px"><strong style="color:#9a7840">"${client.full_name}"</strong> added to your Clients list.</p>`,
      icon: "success",
      timer: 2500,
      showConfirmButton: false,
      background: "#ffffff",
      color: "#0f172a",
    });
  };

  /* ── Create Job ── */
  const handleCreateJob = async () => {
    if (!jobTitle.trim()) return;

    // If lead not yet converted, confirm client creation first
    if (!lead.converted_client_id) {
      const pre = await Swal.fire({
        title: "Create client first?",
        html: `<p style="color:#64748b;font-size:13px">A client record will be created or linked for <strong style="color:#0f172a">${lead.name}</strong> before creating the job.</p>`,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Continue",
        cancelButtonText: "Cancel",
        background: "#ffffff",
        color: "#0f172a",
        confirmButtonColor: "#C8A96E",
        cancelButtonColor: "#64748b",
      });
      if (!pre.isConfirmed) return;
    }

    setSaving(true);
    const { client, error } = await resolveClient(lead, tenantId);
    if (error || !client) {
      Swal.fire({ title: "Error", text: error, icon: "error", background: "#ffffff", color: "#0f172a" });
      setSaving(false);
      return;
    }

    const { data: job, error: jobErr } = await supabase
      .from("jobs")
      .insert({
        tenant_id:  tenantId,
        client_id:  client.id,
        title:      jobTitle.trim(),
        job_type:   jobType,
        status:     "scheduled",
        shoot_date: jobDate ? new Date(jobDate).toISOString() : null,
        location:   jobLocation || null,
        notes:      jobNotes    || null,
      })
      .select("id")
      .maybeSingle();

    if (jobErr || !job) {
      Swal.fire({ title: "Error creating job", text: jobErr?.message, icon: "error", background: "#ffffff", color: "#0f172a" });
      setSaving(false);
      return;
    }

    const jobId = (job as { id: string }).id;
    const steps = DEFAULT_JOB_STEPS.map((s) => ({
      ...s,
      tenant_id: tenantId,
      job_id:    jobId,
      status:    "pending",
    }));
    await supabase.from("job_steps").insert(steps);
    await markConverted(lead, client.id, onLeadUpdate);
    if (client.isNew) setLinkedClient(client);
    logActivity({ tenantId, action: "created", entity: "job", entityId: jobId, entityName: jobTitle.trim() });

    setSaving(false);
    setPanel(null);

    const goResult = await Swal.fire({
      title: "Job created!",
      html: `<p style="color:#64748b;font-size:13px">Job <strong style="color:#9a7840">"${jobTitle}"</strong> created and linked to client <strong>${client.full_name}</strong>.</p>`,
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "<i class=\'ri-arrow-right-line\'></i>&nbsp; View Job",
      cancelButtonText: "Stay here",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#C8A96E",
      cancelButtonColor: "#64748b",
    });
    if (goResult.isConfirmed) navigate(`/admin/jobs/${jobId}`);
  };

  /* ── Create Contract ── */
  const handleCreateContract = async () => {
    if (!ctTitle.trim()) return;
    const total   = parseFloat(ctTotal)   || 0;
    const deposit = parseFloat(ctDeposit) || 0;
    if (deposit > total) return;

    // If lead not yet converted, confirm client creation first
    if (!lead.converted_client_id) {
      const pre = await Swal.fire({
        title: "Create client first?",
        html: `<p style="color:#64748b;font-size:13px">A client record will be created or linked for <strong style="color:#0f172a">${lead.name}</strong> before creating the contract.</p>`,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Continue",
        cancelButtonText: "Cancel",
        background: "#ffffff",
        color: "#0f172a",
        confirmButtonColor: "#C8A96E",
        cancelButtonColor: "#64748b",
      });
      if (!pre.isConfirmed) return;
    }

    setSaving(true);
    const { client, error } = await resolveClient(lead, tenantId);
    if (error || !client) {
      Swal.fire({ title: "Error", text: error, icon: "error", background: "#ffffff", color: "#0f172a" });
      setSaving(false);
      return;
    }

    const contractNum = `CTR-${Date.now().toString().slice(-6)}`;
    const { data: ct, error: ctErr } = await supabase
      .from("contracts")
      .insert({
        tenant_id:       tenantId,
        client_id:       client.id,
        client_name:     lead.name,
        contract_number: contractNum,
        title:           ctTitle.trim(),
        event_date:      lead.event_date ?? "",
        event_location:  ctLocation || "",
        package_name:    ctPackage  || "",
        total_amount:    total,
        deposit_amount:  deposit,
        balance_amount:  Math.max(0, total - deposit),
        status:          "draft",
        payment_status:  "unpaid",
      })
      .select("id")
      .maybeSingle();

    if (ctErr || !ct) {
      Swal.fire({ title: "Error creating contract", text: ctErr?.message, icon: "error", background: "#ffffff", color: "#0f172a" });
      setSaving(false);
      return;
    }

    const contractId = (ct as { id: string }).id;
    await markConverted(lead, client.id, onLeadUpdate);
    if (client.isNew) setLinkedClient(client);
    logActivity({ tenantId, action: "created", entity: "contract", entityId: contractId, entityName: ctTitle.trim() });

    setSaving(false);
    setPanel(null);

    const goResult = await Swal.fire({
      title: "Contract created!",
      html: `<p style="color:#64748b;font-size:13px">Contract <strong style="color:#9a7840">"${contractNum}"</strong> created as draft for client <strong>${client.full_name}</strong>.</p>`,
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "<i class=\'ri-arrow-right-line\'></i>&nbsp; View Contract",
      cancelButtonText: "Stay here",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#C8A96E",
      cancelButtonColor: "#64748b",
    });
    if (goResult.isConfirmed) navigate(`/admin/contracts/${contractId}`);
  };

  /* ── Create Appointment ── */
  const handleCreateAppointment = async () => {
    if (!apptTitle.trim() || !apptDate) return;
    setSaving(true);

    // Optionally link to client_id if lead already converted
    let clientIdForAppt: string | null = null;
    if (lead.converted_client_id) {
      clientIdForAppt = lead.converted_client_id;
    }

    const startAt = new Date(`${apptDate}T${apptTime}:00`).toISOString();
    const { data: appt, error } = await supabase
      .from("appointments")
      .insert({
        tenant_id:        tenantId,
        lead_id:          lead.id,
        client_id:        clientIdForAppt,
        title:            apptTitle.trim(),
        appointment_type: apptType,
        status:           "scheduled",
        start_at:         startAt,
        location:         apptLocation || null,
        notes:            apptNotes    || null,
        show_in_portal:   false,
      })
      .select("id")
      .maybeSingle();

    if (error || !appt) {
      Swal.fire({ title: "Error", text: error?.message, icon: "error", background: "#ffffff", color: "#0f172a" });
      setSaving(false);
      return;
    }

    logActivity({ tenantId, action: "created", entity: "appointment", entityId: (appt as { id: string }).id, entityName: apptTitle.trim() });
    setSaving(false);
    setPanel(null);

    const goResult = await Swal.fire({
      title: "Appointment scheduled!",
      html: `<p style="color:#64748b;font-size:13px">"<strong>${apptTitle}</strong>" added to your calendar on <strong>${new Date(`${apptDate}T${apptTime}:00`).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</strong>.</p>`,
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "<i class=\'ri-calendar-line\'></i>&nbsp; View Calendar",
      cancelButtonText: "Stay here",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#C8A96E",
      cancelButtonColor: "#64748b",
    });
    if (goResult.isConfirmed) navigate("/admin/appointments");
  };

  /* ── UI helpers ── */
  const SaveBtn = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
    <div className="flex justify-end gap-3 pt-3 border-t border-slate-100 mt-3">
      <button
        onClick={() => setPanel(null)}
        className="px-4 py-2 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap"
      >
        Cancel
      </button>
      <button
        onClick={onClick}
        disabled={saving || disabled}
        className="flex items-center gap-2 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      >
        {saving
          ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          : <i className="ri-check-line"></i>
        }
        Confirm
      </button>
    </div>
  );

  const ACTIONS = [
    { id: "convert",     icon: "ri-user-add-line",        label: "Convert to Client",  desc: "Create or link a client record",        color: "bg-[#C8A96E]/10 text-[#9a7840] border-[#C8A96E]/20" },
    { id: "job",         icon: "ri-camera-3-line",        label: "Create Job",         desc: "Schedule a shoot linked to this lead",   color: "bg-violet-50 text-violet-600 border-violet-200" },
    { id: "contract",    icon: "ri-file-text-line",       label: "Create Contract",    desc: "Draft a contract for this event",        color: "bg-emerald-50 text-emerald-600 border-emerald-200" },
    { id: "appointment", icon: "ri-calendar-check-line",  label: "Book Appointment",   desc: "Schedule a consultation or meeting",     color: "bg-sky-50 text-sky-600 border-sky-200" },
  ] as const;

  const isBooked = lead.status === "booked";

  return (
    <div className="space-y-4">

      {/* ── Linked client banner (when already converted) ── */}
      {(lead.converted_client_id || isBooked) && (
        <div className="bg-white rounded-xl border border-emerald-200 overflow-hidden">
          <div className="flex items-start gap-3 p-4">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 shrink-0">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-emerald-700 font-semibold text-sm">Lead converted</p>
              {loadingClient ? (
                <div className="h-3 w-32 bg-emerald-100 rounded animate-pulse mt-1.5"></div>
              ) : linkedClient ? (
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className="text-emerald-600 text-xs">
                    Linked to client <strong className="text-emerald-700">"{linkedClient.full_name}"</strong>
                    {linkedClient.email && <span className="text-emerald-500"> · {linkedClient.email}</span>}
                  </p>
                  <button
                    onClick={() => navigate("/admin/clients")}
                    className="inline-flex items-center gap-1 text-[10px] text-[#9a7840] hover:underline cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-up-line"></i>
                    View in Clients
                  </button>
                </div>
              ) : (
                <p className="text-emerald-600 text-xs mt-0.5">This lead has been booked.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Action cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ACTIONS.map((action) => {
          const isActive = panel === action.id;
          return (
            <button
              key={action.id}
              onClick={() => setPanel(isActive ? null : action.id)}
              className={`text-left p-4 rounded-xl border transition-all cursor-pointer ${
                isActive
                  ? `${action.color} scale-[0.99]`
                  : "bg-white border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-3 transition-all ${
                isActive ? "bg-white/60" : action.color
              }`}>
                <i className={`${action.icon} text-base`}></i>
              </div>
              <p className={`font-semibold text-sm ${isActive ? "" : "text-slate-800"}`}>{action.label}</p>
              <p className={`text-xs mt-0.5 leading-relaxed ${isActive ? "opacity-70" : "text-slate-400"}`}>{action.desc}</p>
            </button>
          );
        })}
      </div>

      {/* ── Panel: Convert ── */}
      {panel === "convert" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 shrink-0">
              <i className="ri-user-add-line text-[#9a7840] text-sm"></i>
            </div>
            <div>
              <h3 className="text-slate-700 font-semibold text-sm">Convert to Client</h3>
              <p className="text-slate-400 text-xs mt-0.5">Checks for duplicates automatically before creating.</p>
            </div>
          </div>

          {/* Dedup preview */}
          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1.5">
            <p className="text-slate-500 text-xs font-medium">Will search by:</p>
            {lead.email && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <i className="ri-mail-line text-slate-400 text-[11px] shrink-0"></i>
                Email: <span className="text-slate-700 font-medium">{lead.email}</span>
              </div>
            )}
            {lead.phone && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <i className="ri-phone-line text-slate-400 text-[11px] shrink-0"></i>
                Phone: <span className="text-slate-700 font-medium">{lead.phone}</span>
              </div>
            )}
            {!lead.email && !lead.phone && (
              <p className="text-amber-600 text-xs">No email or phone — a new client will always be created.</p>
            )}
          </div>

          <div className="p-3 rounded-xl bg-[#C8A96E]/5 border border-[#C8A96E]/15">
            <p className="text-[#9a7840] text-xs leading-relaxed">
              If a matching client is found, the lead will be linked to the existing record instead of creating a duplicate.
              Lead status will be set to <strong>Booked</strong>.
            </p>
          </div>

          <SaveBtn onClick={handleConvert} />
        </div>
      )}

      {/* ── Panel: Job ── */}
      {panel === "job" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-100 shrink-0">
                <i className="ri-camera-3-line text-violet-600 text-sm"></i>
              </div>
              <div>
                <h3 className="text-slate-700 font-semibold text-sm">Create Job</h3>
                <p className="text-slate-400 text-xs mt-0.5">A client will be created or reused automatically.</p>
              </div>
            </div>
            {!lead.converted_client_id && (
              <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg whitespace-nowrap">
                Will create client
              </span>
            )}
          </div>
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Title *</label>
            <input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className={INPUT} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Job Type</label>
              <select value={jobType} onChange={(e) => setJobType(e.target.value)} className={`${INPUT} cursor-pointer`}>
                {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Shoot Date</label>
              <input type="date" value={jobDate} onChange={(e) => setJobDate(e.target.value)} className={`${INPUT} cursor-pointer`} />
            </div>
          </div>
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Location</label>
            <input value={jobLocation} onChange={(e) => setJobLocation(e.target.value)} className={INPUT} placeholder="Venue or address" />
          </div>
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Notes</label>
            <textarea value={jobNotes} onChange={(e) => setJobNotes(e.target.value)} rows={2} maxLength={500} className={`${INPUT} resize-none`} placeholder="Additional notes..." />
          </div>
          <SaveBtn onClick={handleCreateJob} disabled={!jobTitle.trim()} />
        </div>
      )}

      {/* ── Panel: Contract ── */}
      {panel === "contract" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 shrink-0">
                <i className="ri-file-text-line text-emerald-600 text-sm"></i>
              </div>
              <div>
                <h3 className="text-slate-700 font-semibold text-sm">Create Contract</h3>
                <p className="text-slate-400 text-xs mt-0.5">Contract will be created as draft.</p>
              </div>
            </div>
            {!lead.converted_client_id && (
              <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg whitespace-nowrap">
                Will create client
              </span>
            )}
          </div>
          {/* Preloaded data chip */}
          {(lead.event_date || lead.event_type) && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-400 text-[10px] uppercase tracking-wider">Pre-filled from lead:</span>
              {lead.event_type && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] border border-slate-200">
                  <i className="ri-calendar-event-line text-[9px]"></i>
                  {lead.event_type}
                </span>
              )}
              {lead.event_date && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] border border-slate-200">
                  <i className="ri-calendar-line text-[9px]"></i>
                  {fmtDate(lead.event_date)}
                </span>
              )}
            </div>
          )}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Title *</label>
            <input value={ctTitle} onChange={(e) => setCtTitle(e.target.value)} className={INPUT} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Event Location</label>
              <input value={ctLocation} onChange={(e) => setCtLocation(e.target.value)} className={INPUT} placeholder="Venue name" />
            </div>
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Package</label>
              <input value={ctPackage} onChange={(e) => setCtPackage(e.target.value)} className={INPUT} placeholder="e.g. Full Day" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Total ($)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm select-none">$</span>
                <input type="number" min="0" step="0.01" value={ctTotal} onChange={(e) => setCtTotal(e.target.value)} className={`${INPUT} pl-7`} placeholder="0.00" />
              </div>
            </div>
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Deposit ($)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm select-none">$</span>
                <input type="number" min="0" step="0.01" value={ctDeposit} onChange={(e) => setCtDeposit(e.target.value)} className={`${INPUT} pl-7`} placeholder="0.00" />
              </div>
            </div>
          </div>
          {ctTotal && ctDeposit && parseFloat(ctDeposit) <= parseFloat(ctTotal) && (
            <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-xs text-slate-500">
              Balance: <strong className="text-slate-700">${(parseFloat(ctTotal) - parseFloat(ctDeposit)).toFixed(2)}</strong>
            </div>
          )}
          {ctDeposit && ctTotal && parseFloat(ctDeposit) > parseFloat(ctTotal) && (
            <p className="text-red-500 text-xs">Deposit cannot exceed total amount.</p>
          )}
          <SaveBtn onClick={handleCreateContract} disabled={!ctTitle.trim() || (!!ctDeposit && !!ctTotal && parseFloat(ctDeposit) > parseFloat(ctTotal))} />
        </div>
      )}

      {/* ── Panel: Appointment ── */}
      {panel === "appointment" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-sky-100 shrink-0">
                <i className="ri-calendar-check-line text-sky-600 text-sm"></i>
              </div>
              <div>
                <h3 className="text-slate-700 font-semibold text-sm">Book Appointment</h3>
                <p className="text-slate-400 text-xs mt-0.5">
                  Linked to this lead{lead.converted_client_id ? " and client" : ""}.
                </p>
              </div>
            </div>
            {lead.converted_client_id && (
              <span className="text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-lg whitespace-nowrap">
                Client linked
              </span>
            )}
          </div>
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Title *</label>
            <input value={apptTitle} onChange={(e) => setApptTitle(e.target.value)} className={INPUT} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Type</label>
              <select value={apptType} onChange={(e) => setApptType(e.target.value)} className={`${INPUT} cursor-pointer`}>
                {APPT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Date *</label>
              <input type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)} className={`${INPUT} cursor-pointer`} min={new Date().toISOString().split("T")[0]} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Time</label>
              <input type="time" value={apptTime} onChange={(e) => setApptTime(e.target.value)} className={`${INPUT} cursor-pointer`} />
            </div>
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Location</label>
              <input value={apptLocation} onChange={(e) => setApptLocation(e.target.value)} className={INPUT} placeholder="Office, Zoom, etc." />
            </div>
          </div>
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Notes</label>
            <textarea value={apptNotes} onChange={(e) => setApptNotes(e.target.value)} rows={2} maxLength={300} className={`${INPUT} resize-none`} placeholder="Agenda items, preparation..." />
          </div>
          <SaveBtn onClick={handleCreateAppointment} disabled={!apptTitle.trim() || !apptDate} />
        </div>
      )}
    </div>
  );
}